/**
 * /app/$tenantSlug/attendance/import — Import attendance from a biometric
 * machine (migration 083, Channel A; plan docs/46).
 *
 * HR-admin surface (company_admin / hr_manager): upload a device CSV/TXT
 * export → review the auto-detected, editable mapping + matched/unmatched
 * counts → commit. File import is FREE-tier (the pitch: "use your existing
 * biometric machine, free"); the backend gates on time_tracking + hrRole.
 *
 * The page renders inside the shared TimeAndLeaveShell so the Attendance
 * module strip stays visible; a client-side HR gate mirrors the server
 * boundary (a non-HR user 403s on the wire and gets a clean message here).
 */

import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, ShieldX } from "lucide-react";
import { TimeAndLeaveShell } from "@/components/shared/time-and-leave-shell";
import { useHrRoles } from "@/components/hr/use-hr-roles";
import { useTenantSlug } from "@/lib/use-tenant-slug";
import { AttendanceImportWizard } from "@/components/attendance/attendance-import-wizard";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/attendance/import/",
)({
  component: AttendanceImportRoute,
});

function AttendanceImportRoute() {
  const tenantSlug = useTenantSlug();
  const { isHrAdmin } = useHrRoles();

  return (
    <TimeAndLeaveShell
      title="Imports"
      description="Upload an export from your biometric machine. Free on every plan — no device integration required."
      actions={
        <Button asChild variant="outline" className="gap-1.5">
          <Link
            to="/app/$tenantSlug/attendance/import/runs"
            params={{ tenantSlug }}
          >
            Import History
          </Link>
        </Button>
      }
    >
      {isHrAdmin ? (
        <AttendanceImportWizard />
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent text-accent-foreground">
              <ShieldX className="h-6 w-6" />
            </span>
            <p className="max-w-md text-sm text-muted-foreground">
              Only company admins and HR managers can import attendance files.
            </p>
            <Button asChild variant="outline" className="gap-1.5">
              <Link to="/app/$tenantSlug/my-time" params={{ tenantSlug }}>
                <ArrowLeft className="h-4 w-4" />
                Back to My Time
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}
    </TimeAndLeaveShell>
  );
}
